import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';

type Props = {
  value: string;
  onChange: (dataUrl: string) => void;
  disabled?: boolean;
};

/** Simple ink canvas; stores PNG data URL for declaration evidence. */
export function SignaturePad({ value, onChange, disabled = false }: Props) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(Boolean(value));

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const width = canvas.clientWidth || 480;
    const height = canvas.clientHeight || 160;
    canvas.width = Math.floor(width * ratio);
    canvas.height = Math.floor(height * ratio);
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#111827';
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);

    if (value) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, width, height);
        setHasInk(true);
      };
      img.src = value;
    } else {
      setHasInk(false);
    }
  }, [value]);

  function pointFromEvent(event: ReactPointerEvent<HTMLCanvasElement>) {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  }

  function emitPng() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    onChange(canvas.toDataURL('image/png'));
    setHasInk(true);
  }

  function clearPad() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.clientWidth, canvas.clientHeight);
    setHasInk(false);
    onChange('');
  }

  return (
    <div className="af-signature">
      <canvas
        ref={canvasRef}
        className="af-signature__canvas"
        aria-label="Signature pad"
        onPointerDown={(event) => {
          if (disabled) return;
          const point = pointFromEvent(event);
          const ctx = canvasRef.current?.getContext('2d');
          if (!point || !ctx) return;
          drawingRef.current = true;
          canvasRef.current?.setPointerCapture(event.pointerId);
          ctx.beginPath();
          ctx.moveTo(point.x, point.y);
        }}
        onPointerMove={(event) => {
          if (disabled || !drawingRef.current) return;
          const point = pointFromEvent(event);
          const ctx = canvasRef.current?.getContext('2d');
          if (!point || !ctx) return;
          ctx.lineTo(point.x, point.y);
          ctx.stroke();
        }}
        onPointerUp={(event) => {
          if (!drawingRef.current) return;
          drawingRef.current = false;
          canvasRef.current?.releasePointerCapture(event.pointerId);
          emitPng();
        }}
        onPointerLeave={() => {
          if (!drawingRef.current) return;
          drawingRef.current = false;
          emitPng();
        }}
      />
      <div className="af-signature__actions">
        <button type="button" className="btn-secondary btn-sm" onClick={clearPad} disabled={disabled || !hasInk}>
          Clear signature
        </button>
        <span className="af-signature__hint">
          Draw your signature with mouse or finger. Combined with your name, title, date and declaration tick.
        </span>
      </div>
    </div>
  );
}
